import { ABP, ListService, PagedResultDto, TrackByService } from '@abp/ng.core';
import { Confirmation, ConfirmationService } from '@abp/ng.theme.shared';
import { DateAdapter } from '@abp/ng.theme.shared/extensions';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbDateAdapter } from '@ng-bootstrap/ng-bootstrap';
import { filter, finalize, switchMap, tap } from 'rxjs/operators';
import type {
  GetInventoryLocationsInput,
  InventoryLocationWithNavigationPropertiesDto,
} from '../../../proxy/inventory-locations/models';
import { InventoryLocationService } from '../../../proxy/inventory-locations/inventory-location.service';

@Component({
  selector: 'lib-inventory-location',
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [ListService, { provide: NgbDateAdapter, useClass: DateAdapter }],
  templateUrl: './inventory-location.component.html',
  styles: [],
})
export class InventoryLocationComponent implements OnInit {
  data: PagedResultDto<InventoryLocationWithNavigationPropertiesDto> = {
    items: [],
    totalCount: 0,
  };

  filters = {} as GetInventoryLocationsInput;

  form: FormGroup;

  isFiltersHidden = true;

  isModalBusy = false;

  isModalOpen = false;

  selected?: InventoryLocationWithNavigationPropertiesDto;

  constructor(
    public readonly list: ListService,
    public readonly track: TrackByService,
    public readonly service: InventoryLocationService,
    private confirmation: ConfirmationService,
    private fb: FormBuilder
  ) {}

  ngOnInit() {
    const getData = (query: ABP.PageQueryParams) =>
      this.service.getList({
        ...query,
        ...this.filters,
        filterText: query.filter,
      });

    const setData = (list: PagedResultDto<InventoryLocationWithNavigationPropertiesDto>) => (this.data = list);

    this.list.hookToQuery(getData).subscribe(setData);
  }

  clearFilters() {
    this.filters = {} as GetInventoryLocationsInput;
  }

  buildForm() {
    const {
      name,
      description,
      code,
      inventoryLocationId,
    } = this.selected?.inventoryLocation || {};

    this.form = this.fb.group({
      name: [name ?? null, [Validators.required]],
      description: [description ?? null, []],
      code: [code ?? null, []],
      inventoryLocationId: [inventoryLocationId ?? null, []],
    });
  }

  hideForm() {
    this.isModalOpen = false;
    this.form.reset();
  }

  showForm() {
    this.buildForm();
    this.isModalOpen = true;
  }

  submitForm() {
    if (this.form.invalid) return;

    const request = this.selected
      ? this.service.update(this.selected.inventoryLocation.id, this.form.value)
      : this.service.create(this.form.value);

    this.isModalBusy = true;

    request
      .pipe(
        finalize(() => (this.isModalBusy = false)),
        tap(() => this.hideForm()),
      )
      .subscribe(this.list.get);
  }

  create() {
    this.selected = undefined;
    this.showForm();
  }

  update(record: InventoryLocationWithNavigationPropertiesDto) {
    this.selected = record;
    this.showForm();
  }

  delete(record: InventoryLocationWithNavigationPropertiesDto) {
    this.confirmation.warn(
      'Inventory::DeleteConfirmationMessage',
      'Inventory::AreYouSure',
      { messageLocalizationParams: [] }
    ).pipe(
      filter(status => status === Confirmation.Status.confirm),
      switchMap(() => this.service.delete(record.inventoryLocation.id)),
    ).subscribe(this.list.get);
  }
}
